/**
 * MarkAsPaidModal - Modal de baixa manual de parcela
 *
 * Permite ao usuário registrar manualmente o pagamento de uma parcela,
 * informando: data, valor, meio de pagamento recebido, descrição e
 * (opcional) anexo de comprovante.
 *
 * Origem da baixa é registrada como 'manual' e um evento é gravado na
 * timeline (financeiro_webhook_logs) com event_source='manual' + autor.
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-09 v1.0.0 - Criação do modal
 * RELEASE: 2026-06-09 v1.1.0 - Máscara R$ no valor, seletor unificado de meios
 *                              (PaymentMethodSelect), preview de comprovante via
 *                              AttachmentUploadArea+AttachmentThumbnail, e
 *                              meio de recebimento tornado obrigatório.
 * RELEASE: 2026-06-09 v1.2.0 - Encaminha provider_charge_id e conta_corrente_id
 *                              ao hook para sincronizar com provider (Asaas
 *                              receiveInCash) em pagamentos totais.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useMarkChargeAsPaidManual } from '@/hooks/useMarkChargeAsPaidManual';
import { formatCurrency } from '@/lib/currency-utils';
import { CurrencyInput } from '@/components/ui/currency-input';
import { PaymentMethodSelect } from '@/components/financial/PaymentMethodSelect';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import {
  AttachmentThumbnail,
  type BaseAttachment,
} from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { getMimeMediaType } from '@/lib/file-utils';

interface ParcelaForPayment {
  id: string;
  numero_parcela: number;
  valor: number;
  data_vencimento: string;
  /** ID da cobrança no provider (Asaas paymentId). Quando preenchido junto com
   *  conta_corrente_id, a baixa total sincroniza via receive_in_cash. */
  provider_charge_id?: string | null;
  /** Conta corrente associada (resolve credenciais do provider) */
  conta_corrente_id?: string | null;
}

interface MarkAsPaidModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cobrancaId: string;
  parcela: ParcelaForPayment | null;
}


const MAX_RECEIPT_BYTES = 10 * 1024 * 1024; // 10 MB

/**
 * Deriva o tipo de arquivo (image/pdf/document/other) a partir do mime
 * Compatível com getFileIcon/getFileColor do file-utils
 */
function deriveFileType(mime: string): BaseAttachment['file_type'] {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return 'spreadsheet';
  if (mime.startsWith('application/')) return 'document';
  return 'other';
}

export function MarkAsPaidModal({ open, onOpenChange, cobrancaId, parcela }: MarkAsPaidModalProps) {
  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const [dataPagamento, setDataPagamento] = useState(today);
  const [valorPago, setValorPago] = useState<number>(0);
  const [meioPagamentoId, setMeioPagamentoId] = useState<string>('');
  const [descricao, setDescricao] = useState('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mutation = useMarkChargeAsPaidManual();

  // Reset state ao abrir
  useEffect(() => {
    if (open && parcela) {
      setDataPagamento(today);
      setValorPago(parcela.valor);
      setMeioPagamentoId('');
      setDescricao('');
      setReceiptFile(null);
      setReceiptPreviewUrl(null);
      setFileError(null);
    }
  }, [open, parcela, today]);

  // Gera/limpa object URL do preview do comprovante
  useEffect(() => {
    if (!receiptFile) {
      setReceiptPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(receiptFile);
    setReceiptPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [receiptFile]);

  /** Manipula seleção de arquivo via input nativo */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const file = e.target.files?.[0] || null;
    if (file && file.size > MAX_RECEIPT_BYTES) {
      setFileError('Arquivo excede 10 MB');
      // Reseta o input para permitir reseleção do mesmo arquivo
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setReceiptFile(file);
  };

  /** Abre seletor nativo de arquivos */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    if (!parcela) return;
    if (!Number.isFinite(valorPago) || valorPago <= 0) return;
    if (!meioPagamentoId) return; // Obrigatório (v1.1.0)
    await mutation.mutateAsync({
      parcela_id: parcela.id,
      cobranca_id: cobrancaId,
      data_pagamento: dataPagamento,
      valor_pago: valorPago,
      meio_pagamento_id: meioPagamentoId,
      descricao: descricao.trim() || null,
      receipt_file: receiptFile,
      // v1.2.0: dados para sincronização com provider (receive_in_cash)
      valor_parcela_original: parcela.valor,
      provider_charge_id: parcela.provider_charge_id ?? null,
      conta_corrente_id: parcela.conta_corrente_id ?? null,
    });
    onOpenChange(false);
  };


  if (!parcela) return null;

  const submitting = mutation.isPending;
  const isValid =
    Number.isFinite(valorPago) &&
    valorPago > 0 &&
    !!dataPagamento &&
    !!meioPagamentoId;

  // Monta BaseAttachment "fake" para o preview a partir do File selecionado
  const previewAttachment: BaseAttachment | null =
    receiptFile && receiptPreviewUrl
      ? {
          id: 'preview-receipt',
          file_name: receiptFile.name,
          file_type: deriveFileType(receiptFile.type),
          public_url: receiptPreviewUrl,
          thumbnail_url: receiptFile.type.startsWith('image/') ? receiptPreviewUrl : null,
          file_size: receiptFile.size,
        }
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Informações do pagamento
          </DialogTitle>
          <DialogDescription>
            Parcela #{parcela.numero_parcela} — valor original {formatCurrency(parcela.valor)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Data + Valor */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="data-pagamento">Data do pagamento *</Label>
              <Input
                id="data-pagamento"
                type="date"
                value={dataPagamento}
                max={today}
                onChange={(e) => setDataPagamento(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="valor-pago">Valor pago *</Label>
              {/* v1.1.0: máscara monetária R$ via CurrencyInput */}
              <CurrencyInput
                id="valor-pago"
                value={valorPago}
                onChange={setValorPago}
              />
            </div>
          </div>

          {/* Meio de pagamento (v1.1.0: obrigatório + seletor unificado) */}
          {/* v1.2.0 (2026-09-12): mesma regra dos modais de criação/edição —
              lista apenas meios vinculados à conta da parcela OU globais */}
          <div className="space-y-1.5">
            <Label>Meio de recebimento *</Label>
            <PaymentMethodSelect
              value={meioPagamentoId}
              onValueChange={setMeioPagamentoId}
              placeholder="Selecione"
              contaCorrenteId={parcela.conta_corrente_id ?? null}
            />
          </div>

          {/* Descrição */}
          <div className="space-y-1.5">
            <Label htmlFor="descricao">Descrição</Label>
            <Textarea
              id="descricao"
              placeholder="Observações sobre o pagamento (opcional)"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={500}
              rows={2}
            />
          </div>

          {/* Comprovante (v1.1.0: preview/miniatura padrão do sistema) */}
          <div className="space-y-1.5">
            <Label>Comprovante (opcional)</Label>

            {/* Input nativo hidden — controlado por ref */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleFileChange}
            />

            {previewAttachment ? (
              <div className="flex items-start gap-3">
                <AttachmentThumbnail
                  attachment={previewAttachment}
                  onDelete={() => {
                    setReceiptFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  onClick={() => {
                    // v1.3.0: abre preview interno (ImageLightbox) ao invés de
                    // link externo, padronizando com módulos de tarefas/deals.
                    if (receiptPreviewUrl) setLightboxOpen(true);
                  }}
                />
                <div className="flex-1 min-w-0 text-xs text-muted-foreground">
                  <p className="truncate font-medium text-foreground">{previewAttachment.file_name}</p>
                  <p>Clique na miniatura para visualizar</p>
                </div>
              </div>
            ) : (
              <AttachmentUploadArea
                onUploadClick={openFilePicker}
                isUploading={false}
                maxSizeLabel="PDF ou imagem — até 10 MB"
              />
            )}
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Registrando…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                Marcar como paga
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* v1.3.0: preview interno do comprovante (mesmo padrão de Tarefas/Deals) */}
      {receiptFile && receiptPreviewUrl && (
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          mediaItems={[
            {
              url: receiptPreviewUrl,
              type: getMimeMediaType(receiptFile.type) as MediaItem['type'],
              title: receiptFile.name,
            },
          ]}
          initialIndex={0}
        />
      )}
    </Dialog>
  );
}
